import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useDataCache } from '../contexts/DataCacheContext';
import { getLearningPath, saveLearningPath, deleteLearningPath } from '../services/firebase';
import { API_URL } from '../config';
import AIMentor from './AIMentor';
import LearningModeSelector from './LearningModeSelector';
import VoiceMentor from './VoiceMentor';
import './LearningPath.css';

function LearningPath() {
    const [learningPath, setLearningPath] = useState(null);
    const [userProgress, setUserProgress] = useState({
        completedModules: [],
        unlockedModules: [1],
        totalXP: 0
    });
    const [loading, setLoading] = useState(true);
    const [loadingProgress, setLoadingProgress] = useState(0);
    const [loadingStatus, setLoadingStatus] = useState('Starting...');
    const [selectedModule, setSelectedModule] = useState(null);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showVoiceMentor, setShowVoiceMentor] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { currentUser, userData } = useAuth();
    const { prefetchBossBattle, prefetchWorldMap } = useDataCache();

    // Get topic and preGenerated flag from state or localStorage (for refresh support)
    const stateTopic = location.state?.topic;
    const preGenerated = location.state?.preGenerated || false;
    const prebuiltContent = location.state?.prebuiltContent || null;
    const [topic] = useState(() => {
        if (stateTopic) {
            // Save to localStorage for refresh support
            localStorage.setItem('currentLearningPathTopic', stateTopic);
            return stateTopic;
        }
        // Try to recover from localStorage
        return localStorage.getItem('currentLearningPathTopic') || null;
    });

    useEffect(() => {
        if (!topic) {
            navigate('/home');
            return;
        }
        loadLearningPath(preGenerated, prebuiltContent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [topic, currentUser]);

    useEffect(() => {
        if (userData) {
            loadUserProgress();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userData, topic]);

    const loadLearningPath = async (usePreGenerated = false, prebuiltData = null) => {
        setLoading(true);
        setLoadingProgress(0);
        setLoadingStatus('Starting...');

        try {
            if (!currentUser) {
                navigate('/auth');
                return;
            }

            setLoadingProgress(5);
            setLoadingStatus('Checking for saved content...');

            // OPTIMIZATION: If prebuilt content was passed directly, use it immediately
            if (prebuiltData && prebuiltData.learningPath) {
                console.log('⚡ Using prebuilt content passed from Home (instant load!)');
                setLoadingProgress(90);
                setLoadingStatus('Loading prebuilt content...');
                const pathData = prebuiltData.learningPath;

                // Save to Firebase for future loads
                await saveLearningPath(currentUser.uid, topic, pathData);
                setLearningPath(pathData);

                // Prefetch in background
                prefetchBossBattle(topic);
                prefetchWorldMap(topic);
                setLoadingProgress(100);
                setLoading(false);
                return;
            }

            setLoadingProgress(10);
            setLoadingStatus('Checking your saved progress...');

            // Try to load from Firebase first
            const cachedResult = await getLearningPath(currentUser.uid, topic);

            if (cachedResult.success) {
                console.log('📦 Loading cached learning path from Firebase');
                setLoadingProgress(100);
                setLearningPath(cachedResult.data);
                setLoading(false);

                // Prefetch boss battle and world map in background
                prefetchBossBattle(topic);
                prefetchWorldMap(topic);
                return;
            }

            setLoadingProgress(15);
            setLoadingStatus('No cached content found...');

            // If pre-generated content is available, try to fetch it
            if (usePreGenerated) {
                try {
                    console.log('🚀 Checking for pre-generated content...');
                    setLoadingProgress(20);
                    setLoadingStatus('Checking for pre-generated content...');

                    const preGenResponse = await fetch(
                        `${API_URL}/api/generation/content/${currentUser.uid}/${encodeURIComponent(topic)}`
                    );

                    if (preGenResponse.ok) {
                        const preGenData = await preGenResponse.json();
                        if (preGenData.success && preGenData.learningPath) {
                            console.log('✨ Using pre-generated learning path!');
                            setLoadingProgress(90);
                            setLoadingStatus('Loading pre-generated content...');
                            const pathData = preGenData.learningPath;

                            // Save to Firebase for future loads
                            await saveLearningPath(currentUser.uid, topic, pathData);
                            setLearningPath(pathData);

                            // Prefetch in background
                            prefetchBossBattle(topic);
                            prefetchWorldMap(topic);
                            setLoadingProgress(100);
                            setLoading(false);
                            return;
                        }
                    }
                } catch (preGenError) {
                    console.log('Pre-generated content not available, generating fresh');
                }
            }

            // Generate new learning path with Claude (v2)
            console.log('🤖 Generating new multi-level learning path with Claude...');
            setLoadingProgress(25);
            setLoadingStatus('AI is creating your personalized curriculum...');

            // Start a progress simulation while waiting
            const progressInterval = setInterval(() => {
                setLoadingProgress(prev => {
                    // Slowly increment up to 85% while waiting
                    if (prev < 85) return prev + Math.random() * 3;
                    return prev;
                });
            }, 500);

            const response = await fetch(`${API_URL}/api/generate-learning-path-v2`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ topic })
            });

            // Clear the progress simulation
            clearInterval(progressInterval);
            setLoadingProgress(90);
            setLoadingStatus('Finalizing your learning path...');

            const pathData = await response.json();

            // Debug: Log the structure we received
            console.log('📊 Learning path data structure:', pathData);
            console.log('📊 Has levels?', !!pathData.levels);
            if (pathData.levels) {
                console.log('📊 Number of levels:', pathData.levels.length);
                pathData.levels.forEach((level, idx) => {
                    console.log(`📊 Level ${idx + 1}:`, {
                        levelNumber: level.levelNumber,
                        title: level.title,
                        modulesCount: level.modules?.length || 0,
                        hasBoss: !!level.bossModule
                    });
                });
            }

            setLoadingProgress(95);
            setLoadingStatus('Saving to your account...');

            // Save to Firebase
            await saveLearningPath(currentUser.uid, topic, pathData);
            setLearningPath(pathData);

            setLoadingProgress(100);

            // Prefetch boss battle and world map in background for faster loading later
            prefetchBossBattle(topic);
            prefetchWorldMap(topic);
        } catch (error) {
            console.error('Error loading learning path:', error);
            alert('Failed to load learning path. Please try again.');
            navigate('/');
        }
        setLoading(false);
    };

    const loadUserProgress = async () => {
        try {
            if (!userData) return;

            // Use Firebase user data for progress
            const completedModulesArray = userData.completedModules || [];

            // Extract module IDs that match this topic
            const topicCompletedModules = completedModulesArray
                .filter(m => typeof m === 'string' && m.startsWith(`${topic}_`))
                .map(m => parseInt(m.split('_')[1]));

            setUserProgress({
                completedModules: topicCompletedModules,
                unlockedModules: [1, ...topicCompletedModules.map(id => id + 1)],
                totalXP: userData.totalXP || 0
            });
        } catch (error) {
            console.error('Error loading progress:', error);
        }
    };

    const isModuleUnlocked = (module) => {
        // Module is unlocked if:
        // 1. It's in the unlocked list, OR
        // 2. All prerequisites are completed
    
        // Safety check: ensure userProgress exists and has required properties
        if (!userProgress) return module.id === 1; // Only unlock first module if no progress
    
        if (userProgress.unlockedModules && userProgress.unlockedModules.includes(module.id)) {
            return true;
        }
    
        if (!module.prerequisites || module.prerequisites.length === 0) {
            return true;
        }
    
        // Check if completedModules exists before using .includes()
        if (!userProgress.completedModules) {
            return false;
        }
    
        return module.prerequisites.every(prereqId => 
            userProgress.completedModules.includes(prereqId)
        );
    };

    const isModuleCompleted = (moduleId) => {
        return userProgress.completedModules.includes(moduleId);
    };

    const startModule = (module) => {
        if (!isModuleUnlocked(module)) {
            return;
        }

        navigate('/interactive-lesson', {
            state: {
                topic: learningPath.topic,
                moduleId: module.id,
                moduleTitle: module.title,
                moduleDescription: module.description,
                difficulty: module.difficulty
            }
        });
    };

    const getDifficultyColor = (difficulty) => {
        switch(difficulty) {
            case 'beginner': return '#57F287';
            case 'intermediate': return '#FEE75C';
            case 'advanced': return '#ED4245';
            default: return '#5865F2';
        }
    };

    const getDifficultyIcon = (difficulty) => {
        switch(difficulty) {
            case 'beginner': return '⭐';
            case 'intermediate': return '⭐⭐';
            case 'advanced': return '⭐⭐⭐';
            default: return '⭐';
        }
    };

    // Extract learning path content for AI Mentor
    const getLearningPathContentForMentor = () => {
        if (!learningPath) return '';

        let content = `LEARNING PATH: ${learningPath.topic}\n\n`;
        content += `OVERVIEW: ${learningPath.description || 'Comprehensive learning path'}\n\n`;

        if (learningPath.levels) {
            learningPath.levels.forEach(level => {
                content += `\n=== LEVEL ${level.levelNumber}: ${level.title} (${level.difficulty}) ===\n`;
                content += `${level.description}\n\n`;

                if (level.modules) {
                    level.modules.forEach(module => {
                        content += `MODULE ${module.id}: ${module.title}\n`;
                        content += `${module.description}\n`;
                        if (module.concepts) {
                            content += `Key concepts: ${module.concepts.join(', ')}\n`;
                        }
                        content += '\n';
                    });
                }
            });
        } else if (learningPath.modules) {
            learningPath.modules.forEach(module => {
                content += `MODULE ${module.id}: ${module.title}\n`;
                content += `${module.description}\n`;
                if (module.concepts) {
                    content += `Key concepts: ${module.concepts.join(', ')}\n`;
                }
                content += '\n';
            });
        }

        return content;
    };

    const handleDeletePath = async () => {
        if (!currentUser || !topic) return;

        setDeleting(true);
        try {
            const result = await deleteLearningPath(currentUser.uid, topic);
            if (result.success) {
                setShowDeleteConfirm(false);
                navigate('/home');
            } else {
                alert('Failed to delete learning path. Please try again.');
            }
        } catch (error) {
            console.error('Error deleting path:', error);
            alert('Failed to delete learning path. Please try again.');
        }
        setDeleting(false);
    };

    if (loading) {
        return (
            <div className="loading-container" style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100vh',
                padding: '2rem',
                background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
            }}>
                <div style={{
                    background: 'rgba(255,255,255,0.05)',
                    borderRadius: '20px',
                    padding: '3rem',
                    maxWidth: '500px',
                    width: '100%',
                    textAlign: 'center'
                }}>
                    <div className="spinner" style={{ margin: '0 auto 1.5rem' }}></div>
                    <h2 style={{ color: '#fff', marginBottom: '0.5rem', fontSize: '1.5rem' }}>
                        Building Your Learning Path
                    </h2>
                    <p style={{ color: '#a0aec0', marginBottom: '2rem', fontSize: '1rem' }}>
                        {topic}
                    </p>

                    {/* Progress Bar */}
                    <div style={{
                        background: 'rgba(255,255,255,0.1)',
                        borderRadius: '10px',
                        height: '12px',
                        overflow: 'hidden',
                        marginBottom: '1rem'
                    }}>
                        <div style={{
                            background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                            height: '100%',
                            width: `${loadingProgress}%`,
                            borderRadius: '10px',
                            transition: 'width 0.3s ease'
                        }} />
                    </div>

                    <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        color: '#a0aec0',
                        fontSize: '0.9rem'
                    }}>
                        <span>{loadingStatus}</span>
                        <span style={{ fontWeight: 'bold', color: '#667eea' }}>
                            {Math.round(loadingProgress)}%
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    if (!learningPath) {
        return (
            <div className="error-message">
                <h2>Couldn't load learning path</h2>
                <button className="btn btn-primary" onClick={() => navigate('/')}>
                    Go Home
                </button>
            </div>
        );
    }

    return (
        <div className="learning-path-container">
            {/* Header Section */}
            <div className="path-header">
                <div className="header-buttons">
                    <button className="btn-back" onClick={() => navigate('/home')}>
                        ← Home
                    </button>
                    <button
                        className="btn-delete-path"
                        onClick={() => setShowDeleteConfirm(true)}
                        title="Delete this learning path"
                    >
                        🗑️
                    </button>
                </div>
                <div className="path-title-section">
                    <h1 className="path-title">{learningPath.topic}</h1>
                    <p className="path-subtitle">Your personalized learning journey</p>
                    <div className="path-meta">
                        <span className="meta-item">
                            🎯 {learningPath.totalLevels || learningPath.levels?.length || 1} Levels
                        </span>
                        <span className="meta-item">
                            📚 {learningPath.totalModules} Modules
                        </span>
                        <span className="meta-item">
                            ⏱️ {learningPath.estimatedTime}
                        </span>
                        <span className="meta-item">
                            💎 {(() => {
                                // Calculate total XP from all levels
                                if (learningPath.levels) {
                                    return learningPath.levels.reduce((sum, level) => {
                                        const moduleXP = level.modules?.reduce((s, m) => s + (m.xpReward || 0), 0) || 0;
                                        const bossXP = level.bossModule?.xpReward || 0;
                                        return sum + moduleXP + bossXP;
                                    }, 0);
                                }
                                return learningPath.modules?.reduce((sum, m) => sum + (m.xpReward || 0), 0) || 0;
                            })()} Total XP
                        </span>
                    </div>
                </div>
            </div>

            {/* Learning Mode Selector - 3 Ways to Learn */}
            <LearningModeSelector
                topic={learningPath.topic}
                learningPath={learningPath}
                onMentorMode={() => setShowVoiceMentor(true)}
            />

            {/* Progress Overview */}
            <div className="progress-overview">
                <div className="progress-card">
                    <div className="progress-stat">
                        <span className="stat-value">
                            {userProgress.completedModules.length}/{learningPath.totalModules}
                        </span>
                        <span className="stat-label">Modules Completed</span>
                    </div>
                    <div className="progress-bar-horizontal">
                        <div
                            className="progress-fill"
                            style={{
                                width: `${(userProgress.completedModules.length / learningPath.totalModules) * 100}%`
                            }}
                        />
                    </div>
                </div>
                <div className="progress-card">
                    <div className="progress-stat">
                        <span className="stat-value">{userProgress.totalXP}</span>
                        <span className="stat-label">Total XP Earned</span>
                    </div>
                </div>
                <div className="progress-card">
                    <button
                        className="btn btn-primary"
                        onClick={() => {
                            // Flatten modules from all levels for flashcards
                            const allModules = learningPath.levels
                                ? learningPath.levels.flatMap(level => level.modules || [])
                                : learningPath.modules || [];

                            navigate('/flashcards-study', {
                                state: {
                                    topic: learningPath.topic,
                                    modules: allModules
                                }
                            });
                        }}
                        style={{ width: '100%', padding: '1rem' }}
                    >
                        🎴 Study Flashcards
                    </button>
                </div>
            </div>

            {/* Multi-Level Skill Tree */}
            <div className="skill-tree">
                <h2 className="section-title">🗺️ Learning Path</h2>

                {/* Support both new multi-level format and legacy format */}
                {learningPath.levels ? (
                    // New multi-level format
                    <div className="levels-container">
                        {learningPath.levels.map((level, levelIndex) => {
                            // Safety check for level.modules
                            if (!level.modules || level.modules.length === 0) {
                                console.warn(`Level ${level.levelNumber} has no modules:`, level);
                                return null;
                            }

                            return (
                            <div key={level.levelNumber} className="level-section">
                                {/* Level Header */}
                                <div className="level-header">
                                    <div className="level-number">Level {level.levelNumber}</div>
                                    <div className="level-info">
                                        <h3 className="level-title">{level.title}</h3>
                                        <p className="level-description">{level.description}</p>
                                    </div>
                                </div>

                                {/* Level Modules */}
                                <div className="modules-container">
                                    {level.modules.map((module, moduleIndex) => {
                                        const unlocked = isModuleUnlocked(module);
                                        const completed = isModuleCompleted(module.id);

                                        return (
                                            <div key={module.id} className="module-row">
                                                {/* Connection line to previous module */}
                                                {moduleIndex > 0 && (
                                                    <div className={`connection-line ${completed ? 'completed' : ''}`}>
                                                        <div className="line-progress"></div>
                                                    </div>
                                                )}

                                                <div
                                                    className={`module-card ${
                                                        completed ? 'completed' : ''
                                                    } ${
                                                        unlocked ? 'unlocked' : 'locked'
                                                    } ${
                                                        selectedModule === module.id ? 'selected' : ''
                                                    }`}
                                                    onClick={() => {
                                                        if (unlocked) {
                                                            setSelectedModule(module.id);
                                                        }
                                                    }}
                                                >
                                                    {/* Status Icon */}
                                                    <div className="module-status">
                                                        {completed && <span className="status-icon completed">✓</span>}
                                                        {!completed && unlocked && <span className="status-icon unlocked">🔓</span>}
                                                        {!unlocked && <span className="status-icon locked">🔒</span>}
                                                    </div>

                                                    {/* Module Number */}
                                                    <div className="module-number">
                                                        {module.id}
                                                    </div>

                                                    {/* Module Content */}
                                                    <div className="module-content">
                                                        <h3 className="module-title">{module.title}</h3>
                                                        <p className="module-description">{module.description}</p>

                                                        <div className="module-meta">
                                                            <span
                                                                className="difficulty-badge"
                                                                style={{
                                                                    borderColor: getDifficultyColor(module.difficulty),
                                                                    color: getDifficultyColor(module.difficulty)
                                                                }}
                                                            >
                                                                {getDifficultyIcon(module.difficulty)} {module.difficulty}
                                                            </span>
                                                            <span className="xp-badge">
                                                                💎 {module.xpReward} XP
                                                            </span>
                                                        </div>

                                                        {/* Prerequisites */}
                                                        {module.prerequisites && module.prerequisites.length > 0 && !completed && (
                                                            <div className="prerequisites">
                                                                <span className="prereq-label">Requires:</span>
                                                                {module.prerequisites.map(prereqId => {
                                                                    // Find prereq in all levels
                                                                    const allModules = learningPath.levels.flatMap(l => l.modules);
                                                                    const prereq = allModules.find(m => m.id === prereqId);
                                                                    return (
                                                                        <span key={prereqId} className="prereq-tag">
                                                                            {prereq?.title || `Module ${prereqId}`}
                                                                        </span>
                                                                    );
                                                                })}
                                                            </div>
                                                        )}

                                                        {/* Action Button */}
                                                        {unlocked && !completed && (
                                                            <button
                                                                className="btn btn-primary btn-start-module"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    startModule(module);
                                                                }}
                                                            >
                                                                Start Learning →
                                                            </button>
                                                        )}

                                                        {completed && (
                                                            <button
                                                                className="btn btn-secondary btn-review"
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    startModule(module);
                                                                }}
                                                            >
                                                                Review ↻
                                                            </button>
                                                        )}

                                                        {!unlocked && (
                                                            <div className="locked-message">
                                                                🔒 Complete prerequisites to unlock
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}

                                    {/* Boss Battle for this level */}
                                    {level.bossModule && (
                                        <div className="module-row boss-module-row">
                                            <div className={`connection-line ${isModuleCompleted(level.bossModule.id) ? 'completed' : ''}`}>
                                                <div className="line-progress"></div>
                                            </div>

                                            <div
                                                className={`module-card boss-card ${
                                                    isModuleCompleted(level.bossModule.id) ? 'completed' : ''
                                                } ${
                                                    isModuleUnlocked(level.bossModule) ? 'unlocked' : 'locked'
                                                }`}
                                            >
                                                <div className="boss-icon-large">⚔️</div>
                                                <div className="module-content">
                                                    <h3 className="module-title boss-title">
                                                        {level.bossModule.title}
                                                    </h3>
                                                    <p className="module-description">{level.bossModule.description}</p>

                                                    <div className="module-meta">
                                                        <span className="xp-badge boss-xp">
                                                            💎 {level.bossModule.xpReward} XP
                                                        </span>
                                                    </div>

                                                    {isModuleUnlocked(level.bossModule) && !isModuleCompleted(level.bossModule.id) && (
                                                        <button
                                                            className="btn btn-danger btn-start-module"
                                                            onClick={() => navigate('/boss-battle', {
                                                                state: {
                                                                    topic: learningPath.topic,
                                                                    moduleId: level.bossModule.id,
                                                                    levelNumber: level.levelNumber
                                                                }
                                                            })}
                                                        >
                                                            Enter Boss Battle ⚔️
                                                        </button>
                                                    )}

                                                    {isModuleCompleted(level.bossModule.id) && (
                                                        <button
                                                            className="btn btn-secondary btn-review"
                                                            onClick={() => navigate('/boss-battle', {
                                                                state: {
                                                                    topic: learningPath.topic,
                                                                    moduleId: level.bossModule.id,
                                                                    levelNumber: level.levelNumber
                                                                }
                                                            })}
                                                        >
                                                            Retry Boss ↻
                                                        </button>
                                                    )}

                                                    {!isModuleUnlocked(level.bossModule) && (
                                                        <div className="locked-message">
                                                            🔒 Complete all modules in this level
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>
                            );
                        })}
                    </div>
                ) : (
                    // Legacy single-level format (backwards compatibility)
                    <div className="modules-container">
                        {learningPath.modules?.map((module, index) => {
                            const unlocked = isModuleUnlocked(module);
                            const completed = isModuleCompleted(module.id);

                            return (
                                <div key={module.id} className="module-row">
                                    {index > 0 && (
                                        <div className={`connection-line ${completed ? 'completed' : ''}`}>
                                            <div className="line-progress"></div>
                                        </div>
                                    )}

                                    <div
                                        className={`module-card ${completed ? 'completed' : ''} ${unlocked ? 'unlocked' : 'locked'}`}
                                        onClick={() => unlocked && setSelectedModule(module.id)}
                                    >
                                        <div className="module-status">
                                            {completed && <span className="status-icon completed">✓</span>}
                                            {!completed && unlocked && <span className="status-icon unlocked">🔓</span>}
                                            {!unlocked && <span className="status-icon locked">🔒</span>}
                                        </div>
                                        <div className="module-number">{module.id}</div>
                                        <div className="module-content">
                                            <h3 className="module-title">{module.title}</h3>
                                            <p className="module-description">{module.description}</p>
                                            <div className="module-meta">
                                                <span
                                                    className="difficulty-badge"
                                                    style={{
                                                        borderColor: getDifficultyColor(module.difficulty),
                                                        color: getDifficultyColor(module.difficulty)
                                                    }}
                                                >
                                                    {getDifficultyIcon(module.difficulty)} {module.difficulty}
                                                </span>
                                                <span className="xp-badge">💎 {module.xpReward} XP</span>
                                            </div>
                                            {unlocked && !completed && (
                                                <button
                                                    className="btn btn-primary btn-start-module"
                                                    onClick={(e) => { e.stopPropagation(); startModule(module); }}
                                                >
                                                    Start Learning →
                                                </button>
                                            )}
                                            {completed && (
                                                <button
                                                    className="btn btn-secondary btn-review"
                                                    onClick={(e) => { e.stopPropagation(); startModule(module); }}
                                                >
                                                    Review ↻
                                                </button>
                                            )}
                                            {!unlocked && (
                                                <div className="locked-message">🔒 Complete prerequisites to unlock</div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            {/* AI Mentor Chatbox */}
            <AIMentor
                learningPath={learningPath}
                userProgress={userProgress}
            />

            {/* Voice Mentor Mode - Full Screen Experience */}
            {showVoiceMentor && (
                <VoiceMentor
                    learningPath={learningPath}
                    onClose={() => setShowVoiceMentor(false)}
                />
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteConfirm && (
                <div className="delete-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
                    <div className="delete-modal" onClick={e => e.stopPropagation()}>
                        <div className="delete-modal-icon">🗑️</div>
                        <h3>Delete Learning Path?</h3>
                        <p>Are you sure you want to delete "<strong>{learningPath.topic}</strong>"?</p>
                        <p className="delete-warning">This will permanently delete all your progress, lessons, and saved data for this topic.</p>
                        <div className="delete-modal-buttons">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowDeleteConfirm(false)}
                                disabled={deleting}
                            >
                                Cancel
                            </button>
                            <button
                                className="btn btn-danger"
                                onClick={handleDeletePath}
                                disabled={deleting}
                            >
                                {deleting ? 'Deleting...' : 'Delete Forever'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default LearningPath;
